import { Link } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";

export default function TermsPage() {
  usePageTitle("Terms of Service", "HealthWallet terms of service for the community-powered healthcare funding platform.");

  return (
    <div className="min-h-screen" data-testid="page-terms">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6">
        <Link href="/">
          <span className="inline-flex items-center gap-2 text-sm text-muted-foreground mb-6 cursor-pointer" data-testid="link-back-home">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </span>
        </Link>

        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2" data-testid="text-terms-title">
          Terms of Service
        </h1>
        <p className="text-muted-foreground mb-8">Last updated: February 15, 2026</p>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>1. Acceptance of Terms</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                By accessing or using the HealthWallet platform, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing the platform. These Terms of Service constitute a legally binding agreement between you and HealthWallet.
              </p>
              <p>
                HealthWallet reserves the right to modify these terms at any time. We will notify registered users of material changes via email or through the platform. Your continued use of the platform after such modifications constitutes your acceptance of the updated terms. We encourage you to review these terms periodically to stay informed of any changes.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2. Platform Description</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                HealthWallet is a healthcare funding technology platform operating in the Minneapolis-St. Paul metropolitan area. HealthWallet provides technology services only. HealthWallet is not a bank, insurance company, financial institution, money transmitter, or healthcare provider.
              </p>
              <p>
                The platform enables users to create health wallets, receive community contributions, manage payment schedules with healthcare providers, and participate in condition-based community funds. All services provided by HealthWallet are technology facilitation services designed to connect patients, contributors, healthcare providers, and employers within a unified digital ecosystem.
              </p>
              <p>
                HealthWallet does not provide medical advice, diagnoses, treatment recommendations, or any form of clinical care. Users should consult qualified healthcare professionals for all medical decisions. The platform serves solely as a financial coordination tool for healthcare-related expenses.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>3. Fund Handling and Payment Processing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                All funds are processed, held, and disbursed by Stripe, Inc., our third-party payment processor. HealthWallet does not hold, custody, or control user funds at any time. Stripe is responsible for the security and handling of all financial transactions.
              </p>
              <p>
                By using the platform, you acknowledge that your financial transactions are subject to Stripe's terms of service and privacy policy in addition to these terms. HealthWallet is not liable for any errors, delays, or failures in payment processing that are attributable to Stripe or other third-party financial service providers.
              </p>
              <p>
                Wallet balances displayed on the platform reflect information provided by our payment processing partners. While we strive to display accurate and up-to-date balance information, HealthWallet does not guarantee the accuracy of displayed balances and is not responsible for discrepancies between displayed amounts and actual funds held by Stripe.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>4. Payment Schedules</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                Payment schedules are facilitated by participating healthcare providers (hospital partners). HealthWallet provides the technology to track and manage these arrangements. HealthWallet does not extend credit, charge interest, or act as a lender.
              </p>
              <p>
                Payment schedule terms, including duration, frequency, and amounts, are determined through agreement between patients and their healthcare providers. HealthWallet's role is limited to providing the technological infrastructure that enables these arrangements to be tracked, managed, and executed efficiently.
              </p>
              <p>
                Users are responsible for ensuring that scheduled payments are funded and completed on time. Failure to maintain payment schedules may result in actions taken by the healthcare provider, which are outside the control and responsibility of HealthWallet. Any disputes regarding payment schedules should be directed to the participating healthcare provider.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>5. Not Insurance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                HealthWallet is not an insurance product, health benefit plan, or coverage program. Pre-care subscriptions are voluntary savings mechanisms, not insurance premiums. Community funds and wallet contributions are not insurance coverage.
              </p>
              <p>
                No feature of the HealthWallet platform should be construed as an insurance policy, warranty, or guarantee of payment for healthcare services. The platform does not underwrite risk, calculate actuarial tables, or provide any form of risk pooling as defined under applicable insurance regulations.
              </p>
              <p>
                Users should maintain appropriate health insurance coverage independent of their use of the HealthWallet platform. The platform is designed to supplement, not replace, traditional health insurance or government healthcare programs such as Medicare or Medicaid.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>6. Contributions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                Contributions made through the platform (including community support campaigns, emergency care funding, and wallet contributions) are voluntary financial support. Contributions may not be tax-deductible. Contributors should consult a tax professional regarding any tax implications and should not expect a return on their contributions.
              </p>
              <p>
                Once a contribution is successfully processed, it is generally non-refundable. Contributors acknowledge that funds contributed to a wallet or community fund will be used at the discretion of the wallet holder or fund administrator for healthcare-related expenses. HealthWallet does not guarantee how contributed funds will be utilized.
              </p>
              <p>
                HealthWallet provides transparency features to help contributors track the general use of community funds. However, HealthWallet does not audit or verify the specific use of funds by individual wallet holders. Contributors accept the inherent risk that funds may not be used exactly as intended or anticipated.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>7. Employer Programs</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                Employer partnership features are technology tools that facilitate employer contributions to employee health wallets. These are not employer-sponsored health benefit plans under ERISA or similar regulations. Employers are responsible for their own tax and regulatory compliance.
              </p>
              <p>
                HealthWallet does not provide tax advice, benefits administration, or regulatory compliance services to employers. Employers using the platform should consult with qualified legal and tax professionals to ensure their participation complies with all applicable federal, state, and local laws and regulations.
              </p>
              <p>
                The relationship between employers and employees regarding health wallet contributions is governed by the employer's own policies and applicable law, not by HealthWallet. HealthWallet serves only as the technology facilitator for these transactions and has no authority over employer contribution decisions or employment-related matters.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>8. User Responsibilities</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                Users are responsible for maintaining the confidentiality of their account credentials and for all activities that occur under their account. You agree to notify HealthWallet immediately of any unauthorized use of your account or any other breach of security.
              </p>
              <p>
                You agree to provide accurate, current, and complete information when creating your account and using the platform. You are prohibited from using the platform for any unlawful purpose, to solicit others to perform unlawful acts, to violate any regulations or laws, or to infringe upon the rights of others.
              </p>
              <p>
                Users must not attempt to gain unauthorized access to any portion of the platform, other user accounts, or any systems or networks connected to the platform. Any misuse of the platform, including fraudulent wallet creation, deceptive fundraising campaigns, or misrepresentation of medical conditions, may result in immediate account termination and referral to law enforcement.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>9. Limitation of Liability</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                To the maximum extent permitted by applicable law, HealthWallet and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, or goodwill, arising out of or in connection with your use of the platform.
              </p>
              <p>
                HealthWallet's total aggregate liability for all claims arising from or relating to these terms or your use of the platform shall not exceed the total amount of fees paid by you to HealthWallet in the twelve months preceding the event giving rise to the claim. This limitation applies regardless of the form of action, whether in contract, tort, strict liability, or otherwise.
              </p>
              <p>
                HealthWallet does not warrant that the platform will be uninterrupted, error-free, or free of viruses or other harmful components. The platform is provided on an "as is" and "as available" basis without warranties of any kind, whether express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>10. Governing Law</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                These Terms of Service shall be governed by and construed in accordance with the laws of the State of Minnesota, without regard to its conflict of law provisions. Any legal action or proceeding arising under these terms shall be brought exclusively in the state or federal courts located in Hennepin County, Minnesota.
              </p>
              <p>
                You agree to submit to the personal jurisdiction of such courts and waive any objection to the exercise of jurisdiction over you by such courts and any objection to venue in such courts. If any provision of these terms is found to be unenforceable, the remaining provisions shall remain in full force and effect.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>11. Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div>
                <p className="font-medium text-foreground">HealthWallet</p>
                <p>Minneapolis, MN</p>
                <p>Email: legal@healthwallet.com</p>
              </div>
              <p>
                We aim to respond to all inquiries within five business days. For urgent matters related to account security or suspected fraud, please contact us immediately through the platform's support channels.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 p-4 rounded-md bg-muted text-xs text-muted-foreground leading-relaxed">
          This document is for informational purposes. HealthWallet provides technology services; Stripe holds funds; hospitals facilitate payment arrangements.
        </div>
      </div>
    </div>
  );
}
